import { Alert, Anchor, Button, Group, Loader, Stack, Text } from "@mantine/core";
import { checkoutLabels } from "@openreceive/browser/headless";
import { IconArrowLeft } from "@tabler/icons-react";
import { observer } from "mobx-react";
import type React from "react";
import { useEffect } from "react";
import { formatUsdCents } from "../../shop/types.ts";
import type { ShopStore } from "../../stores/shop/ShopStore.ts";
import { CheckoutLinkNote } from "./CheckoutLink.tsx";
import { LightningPanel } from "./LightningPanel.tsx";
import { MethodGrid } from "./MethodGrid.tsx";
import { OrderStrip } from "./OrderStrip.tsx";
import { StatusCard } from "./StatusCard.tsx";
import { SwapDepositPanel } from "./SwapDepositPanel.tsx";
import { TransactionDetailsPanel } from "./TransactionDetailsPanel.tsx";

export const CheckoutStage: React.FC<{ shop: ShopStore }> = observer(({ shop }) => {
  const checkout = shop.checkout;
  const status = checkout.status;
  const swap = checkout.swapDisplay;
  const unavailable = checkout.swapUnavailable;
  const isLightning = checkout.pickerKey?.startsWith("method:") ?? false;
  const hasPanel =
    isLightning || Boolean(swap) || Boolean(unavailable) || Boolean(checkout.startingSwapAsset);
  // While a refund is OWED, the payment column is the refund and nothing else.
  // `backToMethods` DISMISSES the attempt, which here would hide the one screen
  // that can claim the money back behind a method grid — so the breadcrumb goes
  // until the refund is requested. Once it is pending or sent the deposit is
  // finished with, and switching method is how the payer buys after all.
  const refundOwed = swap?.state === "refund_required";
  // The refund screen carries its own, louder copy of the order id, so the
  // quiet checkout note stands down rather than putting the same copy
  // button on the page twice.
  const refunding = swap?.state.startsWith("refund") ?? false;

  // Settlement is proven on the server. When the checkout's own poll says the
  // payment landed, we re-read our own order — the row `config.on_paid` wrote —
  // and only then show the receipt with its downloads.
  //
  // The host's realtime push calls the SAME store method, and usually gets here
  // first; `confirmSettlement` is single-flight so the two do not race.
  // `void` on the floating promise, the guard first, the store doing the work.
  useEffect(() => {
    if (!checkout.settled) return;
    void shop.confirmSettlement();
  }, [checkout.settled, shop]);

  // Prepare locks the amount and lists every way to pay it, without minting
  // anything. It runs HERE rather than in ShopStore.placeOrder because it is
  // this component's checkout: a host that plugs a different payment screen
  // into `renderCheckout` must not pay for a prepare nothing renders.
  // `begin` resets first, so a new reference starts clean.
  useEffect(() => {
    if (!shop.orderReference) return;
    void checkout.begin(shop.orderReference);
  }, [checkout, shop.orderReference]);

  return (
    <>
      {/* Two columns on a desktop, one on a phone. The summary — what is being
          bought, where the payment has got to, the transaction record — is the
          column that does not change when the payer picks a coin; the payment
          column is the one they act in. */}
      <div className="or-shop-stage or-checkout">
        <Stack gap="md" className="or-checkout-summary">
          <OrderStrip shop={shop} />

          <StatusCard status={status} />

          {checkout.errorMessage ? (
            <Alert color="red" variant="light">
              {checkout.errorMessage}
            </Alert>
          ) : null}

        </Stack>

        <div className="or-checkout-pay">
          {checkout.preparing ? (
            <Group gap="sm" py="lg" justify="center">
              <Loader size="sm" color="orGreen" />
              <Text size="sm" c="dimmed">
                {checkoutLabels.preparingPayment}
              </Text>
            </Group>
          ) : hasPanel ? (
            <Stack gap="sm">
              {/* Backwards movement is a breadcrumb, not a step back — and it
                  names what is about to change. */}
              {refundOwed ? null : (
                <Anchor component="button" type="button" size="sm" onClick={checkout.backToMethods}>
                  <Group gap={4} wrap="nowrap">
                    <IconArrowLeft size={14} />
                    {checkoutLabels.switchPaymentMethod}
                  </Group>
                </Anchor>
              )}

              {checkout.swapStartError ? (
                <Alert color="red" variant="light" title={checkoutLabels.swapStartFailedTitle}>
                  {checkout.swapStartError}
                </Alert>
              ) : null}

              {unavailable ? (
                <Alert color="yellow" variant="light" title={unavailable.title}>
                  <Text size="sm">{unavailable.detail}</Text>
                  {unavailable.range ? (
                    <Text size="sm" mt={4}>
                      {unavailable.range}
                    </Text>
                  ) : null}
                  <Text size="sm" mt={4}>
                    {unavailable.hint}
                  </Text>
                </Alert>
              ) : swap ? (
                <SwapDepositPanel checkout={checkout} swap={swap} />
              ) : isLightning ? (
                <LightningPanel checkout={checkout} />
              ) : (
                <Group gap="sm" py="lg" justify="center">
                  <Loader size="sm" color="orGreen" />
                  <Text size="sm" c="dimmed">
                    {checkoutLabels.preparingPaymentAddress}
                  </Text>
                </Group>
              )}
            </Stack>
          ) : (
            <MethodGrid checkout={checkout} />
          )}
        </div>

        {/* Keep the return link after the payment controls in reading order.
            On desktop it sits beneath the summary in the left column. */}
        {refunding ? null : (
          <div className="or-checkout-return">
            <CheckoutLinkNote checkout={checkout} />
          </div>
        )}

        {/* The record of the payment sits under both columns: it is collapsed
            almost always, and on a phone it must not come between the status
            and the thing the payer has to scan. */}
        <div className="or-checkout-record">
          <TransactionDetailsPanel rows={checkout.transactionRows} />
        </div>
      </div>

      <div className="or-shop-footer">
        <Button variant="subtle" size="sm" onClick={shop.startOver}>
          {checkoutLabels.startOver}
        </Button>
        <Text size="sm" c="dimmed">
          {formatUsdCents(shop.orderTotalCents)}
        </Text>
      </div>
    </>
  );
});
